/**
 * Корень интерфейса поверх canvas поля. Бой — основной экран (canvas под этим слоем); здесь слоты: HUD (C3), вкладки
 * (C5), окно из очереди маршрута (C4) и кнопка «Войти через Telegram» у гостя в браузере. Отступы от выреза и кнопок
 * Telegram — CSS `--app-inset-*` (index.html).
 */
import { SoundToggle } from "../audio/SoundToggle";
import { FeedbackButton } from "./FeedbackButton";
import { FullscreenButton } from "./FullscreenButton";
import { LanguageButton } from "./LanguageButton";
import { useLayout } from "../ui/desktop/layout";
import { Hud as DefaultHud } from "../ui/hud/Hud";
import { shallowEqual, useStore, useT } from "./hooks";
import { slotStore, type WindowViewProps } from "./slots";
import { TelegramLoginButton, useTelegramLogin } from "./TelegramLoginButton";
import { appStore, battleStore, closeWindow, currentWindow, isBattlePaused } from "./store";

function FallbackWindow({ route, close }: WindowViewProps) {
  const t = useT();
  return (
    <div class="app-window" role="dialog" aria-modal="true" data-testid={`window-${route.kind}`}>
      <div class="app-window__box">
        <h2>{t(`window.${route.kind}`)}</h2>
        {route.kind === "runEnded" && <p>{t("app.runEnded")}</p>}
        <button type="button" onClick={close}>
          {t("action.continue")}
        </button>
      </div>
    </div>
  );
}

function HudSlot() {
  const snapshot = useStore(battleStore, (s) => s);
  const View = useStore(slotStore, (s) => s.hud) ?? DefaultHud;
  return snapshot ? <View snapshot={snapshot} /> : null;
}

function TabsSlot({ mode }: { mode?: "sheet" | "panel" }) {
  const tab = useStore(appStore, (s) => s.route.tab);
  const View = useStore(slotStore, (s) => s.tabs);
  return View ? <View tab={tab} mode={mode} /> : null;
}

function WindowSlot() {
  const route = useStore(appStore, currentWindow);
  const views = useStore(slotStore, (s) => s.windows);
  if (!route) return null;
  const View = views[route.kind] ?? FallbackWindow;
  return <View key={route.kind} route={route} close={() => closeWindow(route.kind)} />;
}

/** Телефонная раскладка: виджет входа в углу. На десктопе он стоит в правой колонке (`ui/desktop`). */
function LoginSlot() {
  // в Mini App вход тихий по initData; виджет Telegram Login во встроенном браузере Telegram не работает
  const { eligible, login } = useTelegramLogin();
  if (!eligible) return null;
  return (
    <div class="app-login" data-slot="telegram-login" data-testid="login-slot">
      {login && <TelegramLoginButton bot={login.bot} onAuth={login.onAuth} />}
    </div>
  );
}

export function App() {
  const { paused, locale, miniApp, tabOpen } = useStore(
    appStore,
    (s) => ({ paused: isBattlePaused(s), locale: s.locale, miniApp: s.miniApp, tabOpen: s.route.tab !== null }),
    shallowEqual,
  );
  const Desktop = useStore(slotStore, (s) => s.desktop);
  // десктоп-раскладка — только если её зарегистрировали и окно достаточно широкое (C6)
  const desktop = useLayout() === "desktop" && Desktop !== null;
  return (
    <div
      class="app"
      data-testid="app"
      data-layout={desktop ? "desktop" : "phone"}
      data-locale={locale}
      data-paused={paused ? "1" : "0"}
      data-miniapp={miniApp ? "1" : "0"}
      data-tab-open={tabOpen ? "1" : "0"}
    >
      <div class="app-layer" data-slot="hud">
        <HudSlot />
      </div>
      {desktop && Desktop ? (
        <Desktop tabs={<TabsSlot mode="panel" />} />
      ) : (
        <div class="app-layer" data-slot="tabs">
          <TabsSlot />
        </div>
      )}
      {!desktop && <SoundToggle />}
      {!desktop && <LanguageButton />}
      {!desktop && <FeedbackButton />}
      {!desktop && <FullscreenButton />}
      {!desktop && <LoginSlot />}
      <WindowSlot />
    </div>
  );
}
